<script setup lang="ts">
import { PageState } from 'primevue/paginator'
import AppHero from '@/components/app/AppHero.vue'
import MatchmakingFilters from '@/components/data-filters/MatchmakingFilters.vue'
import CardMatchmaking from '@/components/card/CardMatchmaking.vue'
import { useGeoDataStore } from '~/stores/geoData'
import CardGrid from '@/components/layout/CardGrid.vue'
import { useMatchmakingDataStore } from '~/stores/matchmaking'
import { Page } from '~/types/types'
import { usePagination } from '~/composables/pagination'

import AppMap from '@/components/map/AppMap.vue'
import { LayerPresets } from '~/types/map'
useSeoMeta({
  title: () => `Drivmedla - Samarbeta`,
  description: () =>
    'Vill du vara med och ställa om? För att underlätta omställningen till fossilfria transporter erbjuder Drivmedla en funktion för samarbete mellan olika aktörer.',
  ogTitle: () => `Drivmedla - Samarbeta`,
  ogDescription: () =>
    'Vill du vara med och ställa om? För att underlätta omställningen till fossilfria transporter erbjuder Drivmedla en funktion för samarbete mellan olika aktörer.',
  ogLocale: () => 'sv_SE',
  ogSiteName: () => 'Drivmedla',
  ogUrl: () => `${useRuntimeConfig().public.baseURL}${useRoute().fullPath}`,
  ogImage: () => `${useRuntimeConfig().public.baseURL}/_nuxt/assets/_logo-horizontal-GREEN.svg`,
})
const activeTabIndex = ref<number>(0)
const layerPresets: LayerPresets = {
  'infrastructure-opportunities': { visible: true },
}

const matchmakingStore = useMatchmakingDataStore()
const geoDataStore = useGeoDataStore()
const { findOne } = useStrapi()

const { data: pageInfo } = await useAsyncData(
  'matchmaking-page',
  () => findOne<Page>('matchmaking-page', { populate: '*' }),
  { transform: (_pageInfo) => _pageInfo.data }
)

const { data: initialMatchmakingItems } = useAsyncData('matchmaking', async () => {
  await matchmakingStore.initData()
  return matchmakingStore.postsWithFilters
})
const matchmakingItems = computed(() => {
  if (matchmakingStore.posts.length > 0) return matchmakingStore.postsWithFilters
  return initialMatchmakingItems.value
})
const currentPageOptions = ref<PageState>({ first: 0, rows: 20, page: 0 })
const paginatedMatchmakingItems = computed(() => {
  return usePagination(matchmakingItems.value, currentPageOptions.value)
})

const updatePage = (e: PageState) => {
  currentPageOptions.value = e
}

await useAsyncData('counties', () => geoDataStore.initCounties())

const activeFilters = computed(() => matchmakingStore.getSelectedFilters)

const { updateFilterValues } = useGlobalFilters()
const removeChip = (chip: { val: string; identifier: string; label: string }) => {
  let newFilterArray = matchmakingStore.filters.find(
    (filter) => filter.filterIdentifier === chip.identifier
  )?.values
  newFilterArray = newFilterArray?.filter((val) => val !== chip.val)

  updateFilterValues(chip.identifier, newFilterArray || [])
}

const clearAllChips = () => {
  matchmakingStore.resetFilters()
}
</script>
<template>
  <main>
    <AppHero
      :title="pageInfo?.attributes.generalContent.title"
      background-color="#244A44"
      color="#fff"
    >
      <p
        v-if="pageInfo?.attributes.generalContent.ingress"
        class="m-auto mb-4"
        style="max-width: 600px"
      >
        {{ pageInfo?.attributes.generalContent.ingress }}
      </p>
      <NuxtLink v-if="pageInfo?.attributes.generalContent.ctaText" :to="`/samarbeta/skapa-inlagg`">
        <Button
          class="green-button"
          :label="pageInfo?.attributes.generalContent.ctaText"
          severity="secondary"
          rounded
        />
      </NuxtLink>
    </AppHero>
    <LayoutSection>
      <div class="p-0 my-4 md:col-8">
        <p>Exempel på olika typer av inlägg:</p>
        <ul>
          <li class="my-2">
            Din organisation har tillgänglig mark för byggnation av ladd- eller annan fossilfri
            drivmedelsinfrastruktur.
          </li>
          <li class="my-2">
            Din organisation har ett behov av en viss sorts drivmedel för att ställa om
          </li>
          <li class="my-2">Din organisation har fossilfritt drivmedel att erbjuda.</li>
        </ul>

        <p>Filtrera inläggen enligt kategorierna nedan.</p>
      </div>
      <AppChips v-if="activeFilters.length" size="large" class="my-3">
        <AppChipsItem item="Rensa allt" :remove="() => clearAllChips()" type="clear" />
        <AppChipsItem
          v-for="chip in activeFilters"
          :key="chip.val"
          :item="chip.label"
          type="selected"
          :remove="() => removeChip(chip)"
        />
      </AppChips>
      <MatchmakingFilters />
    </LayoutSection>
    <TabView v-model:activeIndex="activeTabIndex" class="tabs">
      <TabPanel header="Visa som lista">
        <LayoutSection>
          <CardGrid v-if="matchmakingItems?.length" :grid="2">
            <CardMatchmaking
              v-for="(matchmakingItem, i) in paginatedMatchmakingItems"
              :key="i"
              :item="matchmakingItem"
            />
          </CardGrid>
          <div v-else class="flex align-items-center justify-content-center h-22rem">
            <AppHeading title="Inga inlägg hittades..." type="h2" size="large" />
          </div>
          <Paginator
            v-if="matchmakingItems && matchmakingItems.length > currentPageOptions.rows"
            :rows="currentPageOptions.rows"
            :total-records="matchmakingItems?.length"
            @page="updatePage($event)"
          />
          <div class="text-center">
            <NuxtLink :to="`/samarbeta/skapa-inlagg`">
              <Button
                class="green-button"
                label="Skapa ett inlägg nu"
                severity="secondary"
                rounded
              />
            </NuxtLink>
          </div>
        </LayoutSection>
      </TabPanel>
      <TabPanel header="Visa på karta">
        <main class="map-wrapper" style="height: 60vh">
          <AppMap v-if="activeTabIndex === 1" :layer-presets="layerPresets" />
        </main>
      </TabPanel>
    </TabView>
  </main>
</template>
<style lang="scss" scoped>
@import 'App.scss';
</style>
